import { ScanErgebnis } from '../components/gruppen/classes/scan-ergebnis';

export const OHRMARKE_SCAN_LENGTH = 10;
export const OHRMARKE_WITH_CHECK_DIGITS_LENGTH = 12;
export const PASS_SCAN_LENGTH = 15;

/**
 * Wait for check digits still in flight when submit/auto-accept happens at exactly 10 raw digits.
 * USB wedge scanners often emit Enter (or trigger length checks) before digits 11–12 arrive.
 */
export const OHRMARKE_CHECK_DIGITS_SETTLE_MS = 100;

/** After accepting/clearing a 10-digit Ohrmarke, ignore trailing check digits that refill the field. */
export const OHRMARKE_TRAILING_IGNORE_MS = 250;

/** Digits only (no country letters). */
export function rawScanDigitCount(raw: string): number {
    return String(raw ?? '').replace(/\D/g, '').length;
}

/**
 * Raw barcode frame that is complete without waiting:
 * 12 = Ohrmarke + check digits, 15 = Pass.
 * Exactly 10 is NOT safe — check digits may still be arriving.
 */
export function isFinishedOhrmarkePassBarcodeLength(digitCount: number): boolean {
    return digitCount === OHRMARKE_WITH_CHECK_DIGITS_LENGTH || digitCount === PASS_SCAN_LENGTH;
}

/**
 * Guards scan text fields against the classic 12-digit ear-tag race:
 * accept/clear at 10, then leftover check digits (2) land in the empty input.
 */
export class OhrmarkeScanTrailGuard {
    private ignoreUntil = 0;
    private settleTimer: ReturnType<typeof setTimeout> | null = null;

    armTrailingIgnore(ms: number = OHRMARKE_TRAILING_IGNORE_MS): void {
        this.ignoreUntil = Date.now() + ms;
    }

    shouldIgnoreIncoming(): boolean {
        return Date.now() < this.ignoreUntil;
    }

    /**
     * If the current raw value is exactly 10 digits, wait briefly for possible check digits,
     * then run. Otherwise run immediately (12/15 already complete, or incomplete length).
     */
    settleThenRun(getRaw: () => string, run: () => void, ms: number = OHRMARKE_CHECK_DIGITS_SETTLE_MS): void {
        this.cancelSettle();
        if (rawScanDigitCount(getRaw()) === OHRMARKE_SCAN_LENGTH) {
            this.settleTimer = setTimeout(() => {
                this.settleTimer = null;
                run();
            }, ms);
            return;
        }
        run();
    }

    cancelSettle(): void {
        if (this.settleTimer != null) {
            clearTimeout(this.settleTimer);
            this.settleTimer = null;
        }
    }

    destroy(): void {
        this.cancelSettle();
        this.ignoreUntil = 0;
    }
}

/**
 * Prepare scanner input for ear-tag (10) or pass (15) handling.
 * Ear-tag barcodes often transmit 10 data digits plus 2 trailing check digits.
 */
export function normalizeOhrmarkePassScanInput(raw: string): string {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
        return '';
    }

    const countryMatch = /^([A-Za-z]{2})(\d{10})$/.exec(trimmed);
    if (countryMatch) {
        return countryMatch[2];
    }

    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === PASS_SCAN_LENGTH) {
        return digitsOnly;
    }

    if (digitsOnly.length === OHRMARKE_WITH_CHECK_DIGITS_LENGTH && digitsOnly.startsWith('27600')) {
        return digitsOnly;
    }

    if (digitsOnly.length === OHRMARKE_WITH_CHECK_DIGITS_LENGTH) {
        return digitsOnly.slice(0, OHRMARKE_SCAN_LENGTH);
    }

    return digitsOnly;
}

export function isValidOhrmarkePassScanLength(value: string): boolean {
    const len = normalizeOhrmarkePassScanInput(value).length;
    return len === OHRMARKE_SCAN_LENGTH || len === PASS_SCAN_LENGTH;
}

export function isInvalidOhrmarkePassScanLength(value: string): boolean {
    const len = normalizeOhrmarkePassScanInput(value).length;
    return len > 0 && !isValidOhrmarkePassScanLength(value);
}

/** User-facing error when scan length is not 10 (Ohrmarke) or 15 (Pass). */
export function getInvalidOhrmarkePassScanLengthMessage(value: string): string {
    const len = normalizeOhrmarkePassScanInput(value).length;
    if (len > OHRMARKE_SCAN_LENGTH && len < PASS_SCAN_LENGTH) {
        return `Fehler! Unvollständiger Pass (${len} von ${PASS_SCAN_LENGTH} Zeichen). Bitte erneut scannen.`;
    }
    return 'Fehler! Bitte eine Ohrmarke mit 10 Zeichen oder einen Pass mit 15 Zeichen eingeben (ggf. langsamer einscannen).';
}

/** Same scan type scanned again (pass after pass or ohrmarke after ohrmarke). */
export function isDuplicateScanType(
    result: { ohrmarke_gescannt?: number; pass_gescannt?: number },
    scannedInput: string,
): boolean {
    const normalized = normalizeOhrmarkePassScanInput(scannedInput);
    if (normalized.length === PASS_SCAN_LENGTH) {
        return Number(result.pass_gescannt) > 1;
    }
    if (normalized.length === OHRMARKE_SCAN_LENGTH) {
        return Number(result.ohrmarke_gescannt) > 1;
    }
    return false;
}

/** Pass and ohrmarke complement each other on an existing group animal. */
export function isComplementScanSuccess(
    result: { ohrmarke_gescannt?: number; pass_gescannt?: number },
    scannedInput: string,
): boolean {
    const normalized = normalizeOhrmarkePassScanInput(scannedInput);
    if (normalized.length === OHRMARKE_SCAN_LENGTH) {
        return Number(result.ohrmarke_gescannt) === 1 && Number(result.pass_gescannt) >= 1;
    }
    if (normalized.length === PASS_SCAN_LENGTH) {
        return Number(result.pass_gescannt) === 1 && Number(result.ohrmarke_gescannt) >= 1;
    }
    return false;
}

/** 10-digit ear tag shared by a pass (15) and an Ohrmarke (10). */
export function tierScanKey(value: string | null | undefined): string {
    const normalized = normalizeOhrmarkePassScanInput(String(value ?? ''));
    if (normalized.length === PASS_SCAN_LENGTH) {
        return normalized.slice(5);
    }
    return normalized;
}

export function isSameTierScan(left: string | null | undefined, right: string | null | undefined): boolean {
    const leftKey = tierScanKey(left);
    const rightKey = tierScanKey(right);
    return leftKey.length === OHRMARKE_SCAN_LENGTH && leftKey === rightKey;
}

export function hasPassScanned(scan: { pass_gescannt?: number } | null | undefined): boolean {
    return Number(scan?.pass_gescannt) > 0;
}

export function hasOhrmarkeScanned(scan: { ohrmarke_gescannt?: number } | null | undefined): boolean {
    return Number(scan?.ohrmarke_gescannt) > 0;
}

/** Prefer 10-digit ear tag for display when both pass and ohrmarke were scanned. */
export function preferScanDisplayId(existing: string, incoming: string): string {
    const existingNormalized = normalizeOhrmarkePassScanInput(existing);
    const incomingNormalized = normalizeOhrmarkePassScanInput(incoming);
    if (incomingNormalized.length === OHRMARKE_SCAN_LENGTH) {
        return String(incoming || '').trim() || String(existing || '').trim();
    }
    if (existingNormalized.length === OHRMARKE_SCAN_LENGTH) {
        return String(existing || '').trim() || String(incoming || '').trim();
    }
    return String(incoming || '').trim() || String(existing || '').trim();
}

export function mergeScanErgebnisse(existing: ScanErgebnis, incoming: ScanErgebnis): ScanErgebnis {
    const merged = Object.assign(new ScanErgebnis(), existing, incoming);
    merged.pass_gescannt = Math.max(Number(existing.pass_gescannt || 0), Number(incoming.pass_gescannt || 0));
    merged.ohrmarke_gescannt = Math.max(
        Number(existing.ohrmarke_gescannt || 0),
        Number(incoming.ohrmarke_gescannt || 0),
    );
    merged.ohrmarke_notnull = preferScanDisplayId(
        String(existing.ohrmarke_notnull || ''),
        String(incoming.ohrmarke_notnull || ''),
    );
    if (!merged.einstallgewicht && existing.einstallgewicht) {
        merged.einstallgewicht = existing.einstallgewicht;
    }
    if (!merged.abteil && existing.abteil) {
        merged.abteil = existing.abteil;
    }
    if (!merged.transpondernummer && existing.transpondernummer) {
        merged.transpondernummer = existing.transpondernummer;
    }
    if (!merged.geschlecht_id && existing.geschlecht_id) {
        merged.geschlecht_id = existing.geschlecht_id;
    }
    if (merged.hat_tierreklamation == null && existing.hat_tierreklamation != null) {
        merged.hat_tierreklamation = existing.hat_tierreklamation;
    }
    return merged;
}
